import type { ThemeTypes } from "@/types/themeTypes/ThemeType";

const SHARED_COLORS = {
    primaryLightest: '#E5F5FF',
    primary1: '#CCEBFF',
    primary2: '#9ED8FF',
    primary3: '#6BC4FF',
    primary5: '#0A9DFF',
    primary7: '#0062A3',

    greyLightest: '#F8FAFC',
    grey1: '#F2F6FA',
    grey2: '#F0F5F9',
    grey3: '#CACFD9',
    grey4: '#7C8FAC',
    grey5: '#5A6A85',

    success1: '#E3F8ED',
    success3: '#A6E8C6',
    success4: '#A6E8C6',
    success5: '#69D89F',
    success6: '#5ecf96',

    infoLightest: '#F1FBFE',
    info1: '#DFF6FC',
    info2: '#C3EEF9',
    info3: '#A3E5F5',
    info4: '#83DBF2',
    info5: '#67D3EF',
    info7: "#16AACF",

    orange1: '#FEEFEB',
    orange5: '#FCA792',

    indigoLightest: '#F4F0FA',
    indigo1: '#E9E1F4',
    indigo5: '#8C62C6',

    amber1: '#FEF2CA',
    amber5: '#F49C2B',

    lime: '#67A228',
    lime1: '#ECFBCE',
    lime3: '#BFF071',
    lime5: '#86CA36',

    teal: '#179488',
    teal1: '#CDFBF1',
    teal3: '#63EAD5',
    teal5: '#20B8A6',
    teal7: '#15766E',

    pink: '#D92876',
    pink1: '#FCE7F3',
    pink5: '#EA4A97',

    purple: '#7543B9',
    purple1: '#E9E1F4',
    purple3: '#BAA1DD',

    errpr1: "#FEEFEB",
    error3: "#FDCBBF",
    error7: "#F84C20",

    whatsappBg: "#f0fdfa",
    whatsappIconBg: "#CDFBF1",
    whatsappPrimary: "#20B8A6",
};

const BLUE_THEME: ThemeTypes = {
    name: "BLUE_THEME",
    dark: false,
    variables: {
        "border-color": "#e5eaef",
        "border-opacity": 1,
    },
    colors: {
        primary: "#0085db",
        secondary: "#707a82",
        info: "#46caeb",
        success: "#4bd08b",
        warning: "#f9dd58",
        error: "#ff0000",
        orange: "#fb977d",
        indigo: "#7543B9",
        lightprimary: "#e5f3fb",
        lightinfo: "#e1f5fa",
        lightsecondary: "#e7ecf0",
        lightsuccess: "#dffff3",
        lighterror: "#ffede9",
        lightwarning: "#fff6ea",
        lightindigo: "#f1ebff",
        textPrimary: "#2A3547",
        textSecondary: "#2A3547",
        borderColor: "#e5eaef",
        inputBorder: "#aeb2b9",
        containerBg: "#ffffff",
        background: "#f0f5f9",
        hoverColor: "#f6f9fc",
        surface: "#fff",
        grey100: "#707a82",
        grey200: "#111c2d",
        darkbg: "#2a3447",
        bglight: "#f5f8fb",
        bgdark: "#111c2d",
        ...SHARED_COLORS,
    },
};

const AQUA_THEME: ThemeTypes = {
    name: "AQUA_THEME",
    dark: false,
    variables: {
        "border-color": "#e5eaef",
        "border-opacity": 1,
    },
    colors: {
        primary: "#0074BA",
        secondary: "#47D7BC",
        info: "#46caeb",
        success: "#4bd08b",
        warning: "#f9dd58",
        error: "#ff0000",
        orange: "#fb977d",
        indigo: "#8763da",
        lightprimary: "#e5f3fb",
        lightinfo: "#e1f5fa",
        lightsecondary: "#e7ecf0",
        lightsuccess: "#dffff3",
        lighterror: "#ffede9",
        lightwarning: "#fff6ea",
        lightindigo: "#f1ebff",
        textPrimary: "#2A3547",
        textSecondary: "#2A3547",
        borderColor: "#e5eaef",
        inputBorder: "#aeb2b9",
        containerBg: "#ffffff",
        background: "#f0f5f9",
        hoverColor: "#f6f9fc",
        surface: "#fff",
        grey100: "#707a82",
        grey200: "#111c2d",
        darkbg: "#2a3447",
        bglight: "#f5f8fb",
        bgdark: "#111c2d",
        ...SHARED_COLORS,
    },
};

const PURPLE_THEME: ThemeTypes = {
    name: "PURPLE_THEME",
    dark: false,
    variables: {
        "border-color": "#e5eaef",
        "border-opacity": 1,
    },
    colors: {
        primary: "#763EBD",
        secondary: "#95CFD5",
        info: "#46caeb",
        success: "#4bd08b",
        warning: "#f9dd58",
        error: "#ff0000",
        orange: "#fb977d",
        indigo: "#8763da",
        lightprimary: "#e5f3fb",
        lightinfo: "#e1f5fa",
        lightsecondary: "#e7ecf0",
        lightsuccess: "#dffff3",
        lighterror: "#ffede9",
        lightwarning: "#fff6ea",
        lightindigo: "#f1ebff",
        textPrimary: "#2A3547",
        textSecondary: "#2A3547",
        borderColor: "#e5eaef",
        inputBorder: "#aeb2b9",
        containerBg: "#ffffff",
        background: "#f0f5f9",
        hoverColor: "#f6f9fc",
        surface: "#fff",
        grey100: "#707a82",
        grey200: "#111c2d",
        darkbg: "#2a3447",
        bglight: "#f5f8fb",
        bgdark: "#111c2d",
        ...SHARED_COLORS,
    },
};

const GREEN_THEME: ThemeTypes = {
    name: "GREEN_THEME",
    dark: false,
    variables: {
        "border-color": "#e5eaef",
        "border-opacity": 1,
    },
    colors: {
        primary: "#0A7EA4",
        secondary: "#CCDA4E",
        info: "#46caeb",
        success: "#4bd08b",
        warning: "#f8c076",
        error: "#ff0000",
        orange: "#fb977d",
        indigo: "#8763da",
        lightprimary: "#e5f3fb",
        lightinfo: "#e1f5fa",
        lightsecondary: "#e7ecf0",
        lightsuccess: "#dffff3",
        lighterror: "#ffede9",
        lightwarning: "#fff6ea",
        lightindigo: "#f1ebff",
        textPrimary: "#2A3547",
        textSecondary: "#2A3547",
        borderColor: "#e5eaef",
        inputBorder: "#aeb2b9",
        containerBg: "#ffffff",
        background: "#f0f5f9",
        hoverColor: "#f6f9fc",
        surface: "#fff",
        grey100: "#707a82",
        grey200: "#111c2d",
        darkbg: "#2a3447",
        bglight: "#f5f8fb",
        bgdark: "#111c2d",
        ...SHARED_COLORS,
    },
};

const CYAN_THEME: ThemeTypes = {
    name: "CYAN_THEME",
    dark: false,
    variables: {
        "border-color": "#e5eaef",
        "border-opacity": 1,
    },
    colors: {
        primary: "#01C0C8",
        secondary: "#FB9678",
        info: "#46caeb",
        success: "#4bd08b",
        warning: "#f8c076",
        error: "#ff0000",
        orange: "#fb977d",
        indigo: "#8763da",
        lightprimary: "#e5f3fb",
        lightinfo: "#e1f5fa",
        lightsecondary: "#e7ecf0",
        lightsuccess: "#dffff3",
        lighterror: "#ffede9",
        lightwarning: "#fff6ea",
        lightindigo: "#f1ebff",
        textPrimary: "#2A3547",
        textSecondary: "#2A3547",
        borderColor: "#e5eaef",
        inputBorder: "#aeb2b9",
        containerBg: "#ffffff",
        background: "#f0f5f9",
        hoverColor: "#f6f9fc",
        surface: "#fff",
        grey100: "#707a82",
        grey200: "#111c2d",
        darkbg: "#2a3447",
        bglight: "#f5f8fb",
        bgdark: "#111c2d",
        ...SHARED_COLORS,
    },
};

const ORANGE_THEME: ThemeTypes = {
    name: "ORANGE_THEME",
    dark: false,
    variables: {
        "border-color": "#e5eaef",
        "border-opacity": 1,
    },
    colors: {
        primary: "#FA896B",
        secondary: "#0074BA",
        info: "#46caeb",
        success: "#4bd08b",
        warning: "#f8c076",
        error: "#ff0000",
        orange: "#fb977d",
        indigo: "#8763da",
        lightprimary: "#e5f3fb",
        lightinfo: "#e1f5fa",
        lightsecondary: "#e7ecf0",
        lightsuccess: "#dffff3",
        lighterror: "#ffede9",
        lightwarning: "#fff6ea",
        lightindigo: "#f1ebff",
        textPrimary: "#2A3547",
        textSecondary: "#2A3547",
        borderColor: "#e5eaef",
        inputBorder: "#aeb2b9",
        containerBg: "#ffffff",
        background: "#f0f5f9",
        hoverColor: "#f6f9fc",
        surface: "#fff",
        grey100: "#707a82",
        grey200: "#111c2d",
        darkbg: "#2a3447",
        bglight: "#f5f8fb",
        bgdark: "#111c2d",
        ...SHARED_COLORS,
    },
};

export {
    BLUE_THEME,
    AQUA_THEME,
    ORANGE_THEME,
    PURPLE_THEME,
    GREEN_THEME,
    CYAN_THEME,
};
